import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Shield, Eye, EyeOff, Lock, User, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import { adminAuthService } from '../../../shared/services/adminAuth'

export default function AdminLoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Check if already authenticated
  useEffect(() => {
    if (adminAuthService.isAuthenticated()) {
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    }
  }, [navigate, location])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await adminAuthService.login({ email, password })

      toast.success('Admin login successful!')

      // Redirect to intended page or dashboard
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } catch (error: any) {
      console.error('Admin login failed:', error)
      toast.error(
        error.message || 'Admin login failed. Please check your credentials.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  // If already authenticated, redirect
  if (adminAuthService.isAuthenticated()) {
    const from = (location.state as any)?.from?.pathname || '/dashboard'
    return <Navigate to={from} replace />
  }

  return (
    <div className="flex min-h-screen flex-col justify-center bg-gradient-to-br from-red-900 via-gray-900 to-black py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex items-center space-x-2">
            <Shield className="h-12 w-12 text-red-500" />
            <span className="text-2xl font-bold text-white">Admin Access</span>
          </div>
        </div>

        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          Shorty.kr Admin
        </h2>
        <p className="mt-2 flex items-center justify-center text-center text-sm text-red-400">
          <AlertTriangle className="mr-1 h-4 w-4" />
          Authorized personnel only
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
      >
        <div className="border border-red-900/50 bg-gray-800 px-4 py-8 shadow-2xl sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-300"
              >
                Admin Email
              </label>
              <div className="relative mt-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <User className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="block w-full appearance-none rounded-lg border border-gray-600 bg-gray-700 py-3 pl-10 pr-3 text-white placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500"
                  placeholder="Enter admin email"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-300"
              >
                Password
              </label>
              <div className="relative mt-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Lock className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="block w-full appearance-none rounded-lg border border-gray-600 bg-gray-700 py-3 pl-10 pr-10 text-white placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500"
                  placeholder="Enter admin password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-500 hover:text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-500 hover:text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative flex w-full justify-center rounded-lg border border-transparent bg-red-600 px-4 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                    Authenticating...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Shield className="mr-2 h-4 w-4" />
                    Admin Login
                  </div>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
              <div className="flex">
                <AlertTriangle className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-red-400" />
                <div className="text-sm text-red-300">
                  <p className="font-medium">Security Notice</p>
                  <p className="mt-1">
                    All admin activities are logged and monitored. Unauthorized
                    access attempts will be reported.
                  </p>
                </div>
              </div>
            </div>

            {/* Development helper */}
            {import.meta.env.DEV && (
              <div className="mt-4 rounded border border-gray-700 p-2 text-xs text-gray-500">
                <p className="font-mono">Dev credentials:</p>
                <p>Email: admin@shorty.kr</p>
                <p>Password: g9T9evQA#wM%</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  )
}
